/**
 * Buddy's proposal against the draft (ai-plan-builder R15 to R19): what it says, which of its changes
 * the trainer's own edits have overtaken, and how the accepted ones enter the tree.
 *
 * ── Where a change points ────────────────────────────────────────────────────────────────────
 * Days and exercises are named by slot token, phases by their POSITION (1-based) in the draft the
 * proposal was made from — the backend's `ProposalPlans`, which builds and applies the same changes
 * on its side. A change carries the fingerprint its target had then (`aiFingerprint.ts`), so a
 * target the trainer has edited, or one that is gone, is recognised:
 *
 *   exerciseAlter / exerciseReplace / exerciseRemove   the exercise's token, its fingerprint
 *   exerciseAdd                                        the day's token and session, the DAY's fingerprint
 *                                                      — none when the day is one this proposal adds
 *   dayAdd                                             the phase's position, its fingerprint
 *   dayRemove                                          the day's token, its fingerprint
 *   phaseAdd                                           the phase it follows (0 = the start), its
 *                                                      fingerprint — none at the start
 *   phaseRemove / phaseCycles                          the phase's position, its fingerprint
 *
 * A position is only trustworthy while the phase standing there is the one the proposal saw, and the
 * fingerprint is what says so: a phase inserted or removed before it puts a different phase at that
 * position, whose stamp differs, so the change reads as outdated rather than landing on the wrong
 * block.
 *
 * ── Dependencies ─────────────────────────────────────────────────────────────────────────────
 * `dependsOn` names the changes a change cannot be applied without. A day and the exercises on it
 * name each other (a day holds at least one exercise, an exercise needs its day), so accepting any of
 * them accepts all, and rejecting one rejects all (R15). An outdated change takes everything that
 * depends on it down with it.
 *
 * ── R18 is enforced where the tree is written ────────────────────────────────────────────────
 * `applyProposalChanges` asks `outdatedChanges` itself and applies none of them, whatever the caller
 * accepted. A gate consulted only by the panel would be one a second caller could forget.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { Draft } from 'immer';

import type {
  TrainWithMeModulesAiDtosResponsesAiProposalChange as ChangeDto,
  TrainWithMeModulesAiDtosResponsesAiProposalExerciseResponse as ProposalExerciseDto,
  TrainWithMeModulesAiDtosResponsesAiProposalResult as ProposalDto,
  TrainWithMeModulesPlansDtosRequestsPostDayTemplateInput as DayInput,
  TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInput as ExerciseInput,
  TrainWithMeModulesPlansDtosRequestsPostPhaseTemplateInput as PhaseInput,
} from '~/shared/api/generated/model';

import {
  dayFingerprint,
  dayRemovalFingerprint,
  exerciseFingerprint,
  fingerprintOfWireNode,
  phaseFingerprint,
  phaseRemovalFingerprint,
} from './aiFingerprint';
import type { ExercisePick } from './draftFactory';
import {
  insertDayDraft,
  insertExerciseDraft,
  insertPhaseDraft,
  removeDay,
  removeExercise,
  removePhase,
  setPhaseCycles,
} from './draftOperations';
import { dayRestSeconds, deriveRestColumns } from './dayRest';
import { COLLECTION_CAPS } from './planCaps';
import { exerciseTypeKeyFromApi } from './planEnums';
import type { DayDraft, ExerciseDraft, PlanDraft, SetDraft } from './planDraft.types';
import { dayFromSaveShape, exerciseFromSaveShape, phaseFromSaveShape, type ExercisePicks } from './saveShapeToDraft';
import { exerciseOrdinals } from './weekProjection';

type MutablePlan = Draft<PlanDraft>;
type MutablePhase = MutablePlan['phases'][number];

interface ChangeBase {
  readonly id: string;
  readonly dependsOn: readonly string[];
  /** The target's fingerprint when the proposal was made; `null` where the change has no target yet. */
  readonly targetFingerprint: string | null;
  /** Buddy's short reason for the change (R15), as sent: model text. */
  readonly reason: string;
  /** Progression the server marked for the trainer to check (R22); it never blocks the change. */
  readonly warnings: readonly AiProposalWarning[];
}

/** A soft warning on a change (R22): the server's sentence, worded in the plan's language. */
export interface AiProposalWarning {
  readonly message: string;
}

export type AiProposalChange =
  | (ChangeBase & {
      readonly kind: 'exerciseAlter' | 'exerciseReplace';
      readonly exerciseSlotToken: string;
      /** The exercise after the change. A replacement carries a new slot token minted by the server. */
      readonly exercise: ExerciseInput;
    })
  | (ChangeBase & { readonly kind: 'exerciseRemove'; readonly exerciseSlotToken: string })
  | (ChangeBase & {
      readonly kind: 'exerciseAdd';
      readonly daySlotToken: string;
      readonly sessionPosition: number;
      readonly exercise: ExerciseInput;
    })
  | (ChangeBase & { readonly kind: 'dayAdd'; readonly phasePosition: number; readonly day: DayInput })
  | (ChangeBase & { readonly kind: 'dayRemove'; readonly daySlotToken: string })
  | (ChangeBase & { readonly kind: 'phaseAdd'; readonly afterPhasePosition: number; readonly phase: PhaseInput })
  | (ChangeBase & { readonly kind: 'phaseRemove'; readonly phasePosition: number })
  | (ChangeBase & { readonly kind: 'phaseCycles'; readonly phasePosition: number; readonly cycleCount: number });

export type AiProposalChangeKind = AiProposalChange['kind'];

export interface AiProposal {
  /** The draft version the proposal was made on; a decision on it names the same one (R13). */
  readonly snapshotVersion: string;
  /** What Buddy says about the proposal as a whole: model text. */
  readonly summary: string;
  /** The waiting proposal this one replaced (R16), or `null`. */
  readonly replacesTurnId: number | null;
  readonly changes: readonly AiProposalChange[];
  /**
   * The library row of every exercise the changes name, as the server sent it with them: what the
   * after-states need to enter the tree (`saveShapeToDraft`) without a read of the library.
   */
  readonly picks: ExercisePicks;
}

// ── Reading the wire ─────────────────────────────────────────────────────────────────────────

function readChange(change: ChangeDto): AiProposalChange | null {
  const { id, kind, target } = change;
  if (id === null || id === '' || target === null) return null;
  const base: ChangeBase = {
    id,
    dependsOn: change.dependsOn,
    targetFingerprint: change.targetFingerprint ?? null,
    reason: change.reason,
    warnings: change.warnings.flatMap((warning) =>
      warning.message === null || warning.message.trim() === '' ? [] : [{ message: warning.message }],
    ),
  };
  const exercise = change.exercise ?? null;
  switch (kind) {
    case 'exerciseAlter':
    case 'exerciseReplace':
      return target.exerciseSlotToken && exercise ? { ...base, kind, exerciseSlotToken: target.exerciseSlotToken, exercise } : null;
    case 'exerciseRemove':
      return target.exerciseSlotToken ? { ...base, kind, exerciseSlotToken: target.exerciseSlotToken } : null;
    case 'exerciseAdd':
      return target.daySlotToken && exercise
        ? { ...base, kind, daySlotToken: target.daySlotToken, sessionPosition: target.sessionPosition ?? 1, exercise }
        : null;
    case 'dayAdd':
      return target.phasePosition != null && change.day ? { ...base, kind, phasePosition: target.phasePosition, day: change.day } : null;
    case 'dayRemove':
      return target.daySlotToken ? { ...base, kind, daySlotToken: target.daySlotToken } : null;
    case 'phaseAdd':
      return target.phasePosition != null && change.phase
        ? { ...base, kind, afterPhasePosition: target.phasePosition, phase: change.phase }
        : null;
    case 'phaseRemove':
      return target.phasePosition != null ? { ...base, kind, phasePosition: target.phasePosition } : null;
    case 'phaseCycles':
      return target.phasePosition != null && change.cycleCount != null
        ? { ...base, kind, phasePosition: target.phasePosition, cycleCount: change.cycleCount }
        : null;
    default:
      // A kind a later backend added. Left out rather than guessed at.
      return null;
  }
}

/**
 * The proposal a turn result carries, or `null` when it carries none. A change this build cannot
 * read is left out, and so is every change that depends on one left out: it could not be applied
 * with what it needs.
 */
export function proposalFromWire(proposal: ProposalDto | null | undefined): AiProposal | null {
  if (proposal == null || !proposal.snapshotVersion) return null;
  let changes = proposal.changes.flatMap((change) => readChange(change) ?? []);
  for (;;) {
    const ids = new Set(changes.map((change) => change.id));
    const kept = changes.filter((change) => change.dependsOn.every((id) => ids.has(id)));
    if (kept.length === changes.length) break;
    changes = kept;
  }
  return {
    snapshotVersion: proposal.snapshotVersion,
    summary: proposal.summary,
    replacesTurnId: proposal.replacesTurnId ?? null,
    changes,
    picks: picksOf(proposal.changes.flatMap((change) => change.exercises)),
  };
}

/** The library rows the changes named, by exercise id; a row without an id or a name is left out. */
function picksOf(exercises: readonly ProposalExerciseDto[]): ExercisePicks {
  return new Map(
    exercises
      .filter((exercise) => exercise.exerciseId > 0 && exercise.exerciseName.trim() !== '')
      .map((exercise): [number, ExercisePick] => [
        exercise.exerciseId,
        {
          id: exercise.exerciseId,
          name: exercise.exerciseName,
          typeKey: exerciseTypeKeyFromApi(exercise.exerciseType),
          measurementFields: exercise.measurementFields,
        },
      ]),
  );
}

// ── Dependencies ─────────────────────────────────────────────────────────────────────────────

function closure(start: Iterable<string>, next: (id: string) => readonly string[]): Set<string> {
  const reached = new Set<string>();
  const pending = [...start];
  for (let id = pending.pop(); id !== undefined; id = pending.pop()) {
    if (reached.has(id)) continue;
    reached.add(id);
    pending.push(...next(id));
  }
  return reached;
}

/** The changes `ids` cannot be applied without, `ids` included: what accepting them accepts. */
export function withDependencies(changes: readonly AiProposalChange[], ids: Iterable<string>): ReadonlySet<string> {
  const byId = new Map(changes.map((change) => [change.id, change]));
  return closure(ids, (id) => byId.get(id)?.dependsOn ?? []);
}

/** The changes that cannot be applied without `ids`, `ids` included: what rejecting them rejects. */
export function withDependents(changes: readonly AiProposalChange[], ids: Iterable<string>): ReadonlySet<string> {
  const dependents = new Map<string, string[]>();
  for (const change of changes) {
    for (const dependency of change.dependsOn) dependents.set(dependency, [...(dependents.get(dependency) ?? []), change.id]);
  }
  return closure(ids, (id) => dependents.get(id) ?? []);
}

// ── Outdated changes (R18) ───────────────────────────────────────────────────────────────────

interface ExerciseSite {
  readonly phaseIndex: number;
  readonly day: DayDraft;
  readonly index: number;
  readonly exercise: ExerciseDraft;
}

function findExerciseSite(draft: PlanDraft, slotToken: string): ExerciseSite | null {
  for (const [phaseIndex, phase] of draft.phases.entries()) {
    for (const day of phase.days) {
      const index = day.exercises.findIndex((exercise) => exercise.slotToken === slotToken);
      const exercise = day.exercises[index];
      if (exercise !== undefined) return { phaseIndex, day, index, exercise };
    }
  }
  return null;
}

function findDaySite(draft: PlanDraft, slotToken: string): { readonly phaseIndex: number; readonly day: DayDraft } | null {
  for (const [phaseIndex, phase] of draft.phases.entries()) {
    const day = phase.days.find((candidate) => candidate.slotToken === slotToken);
    if (day !== undefined) return { phaseIndex, day };
  }
  return null;
}

/** Whether the stamp still matches; a change carrying none asks only that its target is there. */
function stampMatches(change: AiProposalChange, current: () => string): boolean {
  return change.targetFingerprint === null || change.targetFingerprint === current();
}

/** Day tokens a change of this proposal brings into the tree. */
function addedDayTokens(changes: readonly AiProposalChange[]): ReadonlySet<string> {
  return new Set(
    changes.flatMap((change) => {
      if (change.kind === 'dayAdd') return [change.day.slotToken];
      if (change.kind === 'phaseAdd') return (change.phase.days ?? []).map((day) => day.slotToken);
      return [];
    }),
  );
}

function targetStands(change: AiProposalChange, draft: PlanDraft, added: ReadonlySet<string>): boolean {
  switch (change.kind) {
    case 'exerciseAlter':
    case 'exerciseReplace':
    case 'exerciseRemove': {
      const site = findExerciseSite(draft, change.exerciseSlotToken);
      return site !== null && stampMatches(change, () => exerciseFingerprint(site.exercise));
    }
    case 'exerciseAdd': {
      // The draft holds one session per day; the change's day is either in the tree or one this
      // proposal adds, and then the day's own change answers for it.
      if (change.sessionPosition !== 1) return false;
      const site = findDaySite(draft, change.daySlotToken);
      if (site === null) return added.has(change.daySlotToken);
      return stampMatches(change, () => dayFingerprint(site.day));
    }
    case 'dayRemove': {
      // A removal is stamped with what it removes: an edit anywhere inside the day outdates it.
      const site = findDaySite(draft, change.daySlotToken);
      return site !== null && stampMatches(change, () => dayRemovalFingerprint(site.day));
    }
    case 'phaseAdd': {
      if (change.afterPhasePosition === 0) return true;
      const phase = draft.phases[change.afterPhasePosition - 1];
      return phase !== undefined && stampMatches(change, () => phaseFingerprint(phase));
    }
    case 'phaseRemove': {
      const phase = draft.phases[change.phasePosition - 1];
      return phase !== undefined && stampMatches(change, () => phaseRemovalFingerprint(phase));
    }
    case 'dayAdd':
    case 'phaseCycles': {
      const phase = draft.phases[change.phasePosition - 1];
      return phase !== undefined && stampMatches(change, () => phaseFingerprint(phase));
    }
  }
}

/**
 * The ids of the changes that can no longer be applied (R18): their target is gone or the trainer
 * has changed it since the proposal was made — and every change depending on one of those.
 *
 * A change whose result already stands (`presentChanges`) is not overtaken, even though its target
 * no longer reads as proposed against (an apply changed it): what depends on it can still be applied.
 * A removal is the exception. Its result is an absence, which the trainer removing the target leaves
 * too, and that is the trainer's edit winning (R18), so a removal whose target is gone stays outdated.
 */
export function outdatedChanges(proposal: AiProposal, draft: PlanDraft): ReadonlySet<string> {
  const added = addedDayTokens(proposal.changes);
  const present = presentChanges(proposal, draft);
  const written = (change: AiProposalChange) => present.has(change.id) && change.kind !== 'exerciseRemove' && change.kind !== 'dayRemove';
  const overtaken = proposal.changes
    .filter((change) => !written(change) && !targetStands(change, draft, added))
    .map((change) => change.id);
  return withDependents(proposal.changes, overtaken);
}

// ── Changes already in the tree ──────────────────────────────────────────────────────────────

/** Whether a slot token stands anywhere in the tree, as a day or as an exercise. */
function tokenStands(draft: PlanDraft, slotToken: string): boolean {
  return findDaySite(draft, slotToken) !== null || findExerciseSite(draft, slotToken) !== null;
}

/**
 * The ids of the changes whose result already stands in the tree: what an apply wrote, still there.
 * An addition is recognised by the slot token the server minted for it, which nothing else carries;
 * a changed exercise by its after-state's fingerprint; a removal by its target being gone; a phase's
 * length by the phase standing at its position with that length and a stamp other than the one
 * proposed against.
 *
 * Applying such a change again would put the same slot token in the tree twice (a save then refuses
 * `duplicate_slot_token`), so the apply leaves them out, and the preview reads them as applied.
 */
export function presentChanges(proposal: AiProposal, draft: PlanDraft): ReadonlySet<string> {
  const present = proposal.changes.filter((change) => {
    switch (change.kind) {
      case 'exerciseAdd':
      case 'exerciseReplace':
        return tokenStands(draft, change.exercise.slotToken) && (change.kind === 'exerciseAdd' || !tokenStands(draft, change.exerciseSlotToken));
      case 'exerciseAlter': {
        const site = findExerciseSite(draft, change.exerciseSlotToken);
        if (site === null) return false;
        const stamp = exerciseFingerprint(site.exercise);
        return stamp !== change.targetFingerprint && stamp === fingerprintOfWireNode('exercise', { ...change.exercise });
      }
      case 'exerciseRemove':
        return !tokenStands(draft, change.exerciseSlotToken);
      case 'dayAdd':
        return tokenStands(draft, change.day.slotToken);
      case 'dayRemove':
        return !tokenStands(draft, change.daySlotToken);
      case 'phaseAdd':
        return (change.phase.days ?? []).some((day) => tokenStands(draft, day.slotToken));
      case 'phaseRemove':
        return false;
      case 'phaseCycles': {
        const phase = draft.phases[change.phasePosition - 1];
        return phase !== undefined && phase.cycles === change.cycleCount && phaseFingerprint(phase) !== change.targetFingerprint;
      }
    }
  });
  return new Set(present.map((change) => change.id));
}

// ── Which applied changes are still applied ──────────────────────────────────────────────────

/**
 * What this document session knows of one proposal's applies, by change id: `true` while an apply
 * of it stands in the history (made, or made again by a redo), `false` once an undo took it back.
 * A change this session never applied has no entry. Kept by the document store beside the undo
 * stack, never inferred from the tree: being changed since the proposal is no evidence of having
 * been applied.
 */
export type AiApplyStanding = ReadonlyMap<string, boolean>;

/** Every proposal's `AiApplyStanding`, by `proposalKey`. */
export type AiApplyRecords = ReadonlyMap<string, AiApplyStanding>;

export const NO_AI_APPLY_RECORDS: AiApplyRecords = new Map();

export const NO_AI_APPLY_STANDING: AiApplyStanding = new Map();

/** The key a proposal's applies are kept under: the draft version it was made on. */
export function proposalKey(proposal: AiProposal): string {
  return proposal.snapshotVersion;
}

/** One apply, as an undo entry carries it: what an undo takes back and a redo puts back. */
export interface AiApplyRecord {
  readonly proposalKey: string;
  readonly changeIds: readonly string[];
}

/** The records after an apply is made or redone (`standing` true), or undone (`false`). */
export function withApplyStanding(records: AiApplyRecords, apply: AiApplyRecord, standing: boolean): AiApplyRecords {
  const next = new Map(records.get(apply.proposalKey) ?? NO_AI_APPLY_STANDING);
  for (const id of apply.changeIds) next.set(id, standing);
  return new Map(records).set(apply.proposalKey, next);
}

/**
 * Of a proposal's changes, the ones applied now (R19): what the card reads as applied and counts.
 *
 * · Applied in this session (`inSession`): applied while the apply stands, whatever the trainer did
 *   to its result since. An exercise Buddy altered and the trainer then set back to its old values by
 *   hand stays applied: the apply was made and not undone. Undone, it is not applied, whatever the
 *   trainer edits next.
 * · Not seen by this session (a reload, another tab) but recorded applied on the server (`recorded`):
 *   applied when its result stands (`presentChanges`) or its target has moved since the proposal
 *   (`outdatedChanges`; a phase removal reads so, another phase or none standing at its position).
 *   Without the history the tree cannot tell an apply edited afterwards from an apply undone and the
 *   area edited afterwards; it reads both as applied. Set back to exactly what was proposed against,
 *   it reads as not applied, and applying it again writes it once.
 */
export function appliedChanges(
  proposal: AiProposal,
  draft: PlanDraft,
  inSession: AiApplyStanding,
  recorded: Iterable<string>,
): ReadonlySet<string> {
  const onServer = new Set(recorded);
  const present = presentChanges(proposal, draft);
  const outdated = outdatedChanges(proposal, draft);
  return new Set(
    proposal.changes
      .map((change) => change.id)
      .filter((id) => inSession.get(id) ?? (onServer.has(id) && (present.has(id) || outdated.has(id)))),
  );
}

// ── What a change is about, for the preview (R15) ────────────────────────────────────────────

/** The day a change is shown under: one of the draft's, or one this proposal adds. */
export interface AiProposalDay {
  readonly slotToken: string;
  /** The day's own name; `null` when the trainer gave it none. */
  readonly name: string | null;
  /** 1-based place among its phase's days. */
  readonly position: number;
  readonly added: boolean;
  /** The day's own rest between sets, in seconds; `null` for the plan's default (and a new day). */
  readonly restSeconds: number | null;
}

/** What the preview needs to say about one change, read off the draft as it stands. */
export interface AiProposalChangeFacts {
  readonly change: AiProposalChange;
  /** `null` for a change to a phase. */
  readonly day: AiProposalDay | null;
  /** The exercise as the draft holds it now: what an alter, a replacement or a removal starts from. */
  readonly before: ExerciseDraft | null;
  /** The exercise the change writes, read with the proposal's library rows; `null` when it writes none. */
  readonly after: ExerciseDraft | null;
  /** The phase the change is about: the draft's for a removal, a new length or a new day, the new one's for an addition. */
  readonly phase: { readonly name: string; readonly cycles: number } | null;
  /** Where a new phase stands: the names of the draft's phases before and after it. */
  readonly between: { readonly after: string | null; readonly before: string | null } | null;
  /** The day a new day comes after, where it is inserted; `null` when it comes first, and for any other change. */
  readonly follows: AiProposalDay | null;
  /**
   * The builder's position marker for the exercise the change writes (`1`, `2a`), from the same walk
   * the day form numbers by (`exerciseOrdinals`): an alter's or a replacement's own, an addition's
   * at the end of its day once the exercises this proposal removes are gone and the additions before
   * it have landed. `null` for a warm-up, for a change to no exercise, and where the exercise or the
   * day is gone.
   */
  readonly ordinal: string | null;
}

function dayAt(draft: PlanDraft, slotToken: string): AiProposalDay | null {
  for (const phase of draft.phases) {
    const index = phase.days.findIndex((day) => day.slotToken === slotToken);
    const day = phase.days[index];
    if (day !== undefined) return { slotToken, name: day.name, position: index + 1, added: false, restSeconds: day.restSeconds };
  }
  return null;
}

function addedDay(proposal: AiProposal, draft: PlanDraft, slotToken: string): AiProposalDay | null {
  for (const change of proposal.changes) {
    if (change.kind !== 'dayAdd' || change.day.slotToken !== slotToken) continue;
    const phase = draft.phases[change.phasePosition - 1];
    return { slotToken, name: change.day.name ?? null, position: (phase?.days.length ?? 0) + 1, added: true, restSeconds: null };
  }
  return null;
}

/** The day a `dayAdd` of this proposal writes, read as the builder will insert it; `null` when none does. */
function addedDayDraft(proposal: AiProposal, draft: PlanDraft, slotToken: string): DayDraft | null {
  for (const change of proposal.changes) {
    if (change.kind !== 'dayAdd' || change.day.slotToken !== slotToken) continue;
    return dayFromSaveShape(change.day, proposal.picks, draft.phases[change.phasePosition - 1]?.startCycle ?? 1);
  }
  return null;
}

function readAfter(proposal: AiProposal, draft: PlanDraft, change: AiProposalChange, before: ExerciseDraft | null, dayToken: string | null): ExerciseDraft | null {
  if (change.kind !== 'exerciseAlter' && change.kind !== 'exerciseReplace' && change.kind !== 'exerciseAdd') return null;
  const pick = change.kind === 'exerciseAlter' ? (before === null ? undefined : pickOf(before)) : proposal.picks.get(change.exercise.exerciseId);
  if (pick === undefined) return null;
  const phaseIndex = dayToken === null ? -1 : draft.phases.findIndex((phase) => phase.days.some((day) => day.slotToken === dayToken));
  const startCycle = draft.phases[phaseIndex]?.startCycle ?? 1;
  return exerciseFromSaveShape(change.exercise, pick, change.kind === 'exerciseAlter' ? (before?.activationCycle ?? null) : null, startCycle);
}

/**
 * Each change with what the preview shows about it: the day it belongs to, the exercise before and
 * after, the phase. Read against the draft as it is now; a change whose target is gone reads with
 * `before` and `day` empty (it is outdated, see `outdatedChanges`).
 */
export function proposalFacts(proposal: AiProposal, draft: PlanDraft): readonly AiProposalChangeFacts[] {
  const facts = proposal.changes.map((change) => factsOf(proposal, draft, change));
  const days = projectedDays(proposal, draft, facts);
  return facts.map((each): AiProposalChangeFacts => {
    const { change, after, day } = each;
    if ((change.kind !== 'exerciseAlter' && change.kind !== 'exerciseReplace' && change.kind !== 'exerciseAdd') || after === null || day === null) return each;
    const projected = days.get(day.slotToken);
    return {
      ...each,
      // As the day will hold it once applied: its rest column read the way the builder reads it.
      after: projected?.exercises.find((exercise) => exercise.slotToken === after.slotToken) ?? after,
      ordinal: projected?.ordinals.get(after.slotToken) ?? null,
    };
  });
}

/**
 * Every day the proposal's exercise changes touch, as the builder will hold it once the whole
 * proposal is applied: what stands, without the exercises the proposal removes, each alter and
 * replacement in its target's place, then the additions in the order the proposal lists them. Its
 * rest columns are derived as `insertExerciseDraft` derives them (`deriveRestColumns` against the
 * day's rest), and its markers are the day form's (`exerciseOrdinals`). One projection per day, so
 * every change on it is numbered on the same list.
 */
function projectedDays(
  proposal: AiProposal,
  draft: PlanDraft,
  facts: readonly AiProposalChangeFacts[],
): ReadonlyMap<string, { readonly exercises: readonly ExerciseDraft[]; readonly ordinals: ReadonlyMap<string, string | null> }> {
  const removed = new Set(facts.flatMap(({ change }) => (change.kind === 'exerciseRemove' ? [change.exerciseSlotToken] : [])));
  const swapped = new Map(
    facts.flatMap(({ change, after }) =>
      (change.kind === 'exerciseAlter' || change.kind === 'exerciseReplace') && after !== null ? [[change.exerciseSlotToken, after] as const] : [],
    ),
  );
  const days = new Map<string, { readonly exercises: readonly ExerciseDraft[]; readonly ordinals: ReadonlyMap<string, string | null> }>();
  for (const { day } of facts) {
    if (day === null) continue;
    if (days.has(day.slotToken)) continue;
    // A day this proposal adds starts from the exercises its own `dayAdd` brings.
    const site = findDaySite(draft, day.slotToken)?.day ?? addedDayDraft(proposal, draft, day.slotToken);
    const standing = (site?.exercises ?? [])
      .filter((exercise) => !removed.has(exercise.slotToken))
      .map((exercise) => swapped.get(exercise.slotToken) ?? exercise);
    const arriving = facts.flatMap(({ change, after }) =>
      change.kind === 'exerciseAdd' && change.daySlotToken === day.slotToken && after !== null ? [after] : [],
    );
    const exercises = deriveRestColumns([...standing, ...arriving], dayRestSeconds(site?.restSeconds ?? day.restSeconds));
    days.set(day.slotToken, { exercises, ordinals: exerciseOrdinals(exercises) });
  }
  return days;
}

function factsOf(proposal: AiProposal, draft: PlanDraft, change: AiProposalChange): AiProposalChangeFacts {
  const empty = { change, day: null, before: null, after: null, phase: null, between: null, follows: null, ordinal: null };
  switch (change.kind) {
    case 'exerciseAlter':
    case 'exerciseReplace':
    case 'exerciseRemove': {
      const site = findExerciseSite(draft, change.exerciseSlotToken);
      const before = site?.exercise ?? null;
      const dayToken = site?.day.slotToken ?? null;
      return {
        ...empty,
        day: dayToken === null ? null : dayAt(draft, dayToken),
        before,
        after: readAfter(proposal, draft, change, before, dayToken),
      };
    }
    case 'exerciseAdd': {
      const day = dayAt(draft, change.daySlotToken) ?? addedDay(proposal, draft, change.daySlotToken);
      return { ...empty, day, after: readAfter(proposal, draft, change, null, day?.added === true ? null : change.daySlotToken) };
    }
    case 'dayAdd': {
      const phase = draft.phases[change.phasePosition - 1];
      // Where `insertDayDraft` puts it: by weekday on a week plan, at the end of the cycle otherwise.
      const weekday = draft.mode === 'date' ? (change.day.dayOfWeek ?? null) : null;
      const last = phase?.days.filter((day) => weekday === null || (day.dayOfWeek ?? 0) <= weekday).at(-1);
      return {
        ...empty,
        day: addedDay(proposal, draft, change.day.slotToken),
        phase: phase === undefined ? null : { name: phase.name, cycles: phase.cycles },
        follows: last === undefined ? null : dayAt(draft, last.slotToken),
      };
    }
    case 'dayRemove':
      return { ...empty, day: dayAt(draft, change.daySlotToken) };
    case 'phaseAdd':
      return {
        ...empty,
        phase: { name: change.phase.name, cycles: change.phase.cycleCount ?? 1 },
        between: {
          after: draft.phases[change.afterPhasePosition - 1]?.name ?? null,
          before: draft.phases[change.afterPhasePosition]?.name ?? null,
        },
      };
    case 'phaseRemove':
    case 'phaseCycles': {
      const phase = draft.phases[change.phasePosition - 1];
      return { ...empty, phase: phase === undefined ? null : { name: phase.name, cycles: phase.cycles } };
    }
  }
}

// ── Applying (R17, R19) ──────────────────────────────────────────────────────────────────────

/**
 * The order the changes are made in. The backend's (`ProposalPlans.Rank`), with one difference:
 * additions come before removals, so a proposal that takes the last exercise out of a phase while
 * adding one to it is not refused by the builder's last-exercise guard halfway through. The tree
 * they leave is the same.
 */
const ORDER: Readonly<Record<AiProposalChangeKind, number>> = {
  phaseCycles: 0,
  exerciseAlter: 1,
  exerciseReplace: 1,
  dayAdd: 2,
  exerciseAdd: 3,
  exerciseRemove: 4,
  dayRemove: 5,
  phaseAdd: 6,
  phaseRemove: 7,
};

/** Every exercise id the change brings in that the tree does not already know. */
function arrivingExerciseIds(change: AiProposalChange): readonly number[] {
  switch (change.kind) {
    case 'exerciseReplace':
    case 'exerciseAdd':
      return [change.exercise.exerciseId];
    case 'dayAdd':
      return (change.day.sessions ?? []).flatMap((session) => (session.exercises ?? []).map((exercise) => exercise.exerciseId));
    case 'phaseAdd':
      return (change.phase.days ?? []).flatMap((day) =>
        (day.sessions ?? []).flatMap((session) => (session.exercises ?? []).map((exercise) => exercise.exerciseId)),
      );
    default:
      return [];
  }
}

/** The library row an exercise already in the tree stands for. */
function pickOf(exercise: ExerciseDraft): ExercisePick {
  return { id: exercise.exerciseId, name: exercise.name, typeKey: exercise.typeKey, measurementFields: exercise.measurementFields };
}

function exerciseCount(draft: PlanDraft, slotToken: string): number {
  let count = 0;
  for (const phase of draft.phases) {
    for (const day of phase.days) count += day.exercises.filter((exercise) => exercise.slotToken === slotToken).length;
  }
  return count;
}

/**
 * Swap the exercise at a site for its after-state, at the same position. The after-state goes in
 * first and the old one comes out after, so a superset keeps its other member (a removal first would
 * dissolve the pair) and a phase's last exercise can be replaced; only a full day takes the removal
 * first, because there is no room for the arrival.
 */
function swapExercise(draft: MutablePlan, site: ExerciseSite, next: ExerciseDraft): boolean {
  if (site.day.exercises.length < COLLECTION_CAPS.exercisesPerSession.max) {
    const inserted = insertExerciseDraft(draft, site.day.slotToken, next, site.index + 1);
    // The first exercise carrying the token is the old one, also when the token is kept (an alter).
    removeExercise(draft, site.exercise.slotToken);
    return inserted;
  }
  removeExercise(draft, site.exercise.slotToken);
  return insertExerciseDraft(draft, site.day.slotToken, next, site.index);
}

function applyOne(
  draft: MutablePlan,
  change: AiProposalChange,
  phasesAsProposed: readonly MutablePhase[],
  picks: ExercisePicks,
): boolean {
  const phaseIndexAt = (position: number): number => {
    const phase = phasesAsProposed[position - 1];
    return phase === undefined ? -1 : draft.phases.indexOf(phase);
  };
  switch (change.kind) {
    case 'phaseCycles': {
      const index = phaseIndexAt(change.phasePosition);
      setPhaseCycles(draft, index, change.cycleCount);
      return draft.phases[index]?.cycles === change.cycleCount;
    }
    case 'exerciseAlter':
    case 'exerciseReplace': {
      const site = findExerciseSite(draft, change.exerciseSlotToken);
      if (site === null) return false;
      const altered = change.kind === 'exerciseAlter';
      const pick = altered ? pickOf(site.exercise) : picks.get(change.exercise.exerciseId);
      const startCycle = draft.phases[site.phaseIndex]?.startCycle ?? 1;
      if (pick === undefined) return false;
      const read = exerciseFromSaveShape(change.exercise, pick, altered ? site.exercise.activationCycle : null, startCycle);
      // An alter is the same slot: what the client was given at it, the server's reading of its window
      // and the columns the trainer opened stay with it. A replacement is a new slot with none of them.
      const next = altered
        ? { ...read, executions: site.exercise.executions, serverCycles: site.exercise.serverCycles, revealedColumns: site.exercise.revealedColumns }
        : read;
      if (!swapExercise(draft, site, next)) return false;
      return altered
        ? exerciseCount(draft, change.exerciseSlotToken) === 1
        : exerciseCount(draft, change.exerciseSlotToken) === 0 && exerciseCount(draft, change.exercise.slotToken) === 1;
    }
    case 'exerciseAdd': {
      const site = findDaySite(draft, change.daySlotToken);
      const pick = picks.get(change.exercise.exerciseId);
      if (site === null || pick === undefined) return false;
      const startCycle = draft.phases[site.phaseIndex]?.startCycle ?? 1;
      return insertExerciseDraft(draft, change.daySlotToken, exerciseFromSaveShape(change.exercise, pick, null, startCycle), null);
    }
    case 'exerciseRemove':
      removeExercise(draft, change.exerciseSlotToken);
      return exerciseCount(draft, change.exerciseSlotToken) === 0;
    case 'dayAdd': {
      const index = phaseIndexAt(change.phasePosition);
      const phase = draft.phases[index];
      const day = phase === undefined ? null : dayFromSaveShape(change.day, picks, phase.startCycle);
      return day !== null && insertDayDraft(draft, index, day);
    }
    case 'dayRemove':
      removeDay(draft, change.daySlotToken);
      return findDaySite(draft, change.daySlotToken) === null;
    case 'phaseAdd': {
      const follows = change.afterPhasePosition === 0 ? -1 : phaseIndexAt(change.afterPhasePosition);
      if (change.afterPhasePosition !== 0 && follows === -1) return false;
      let at = follows + 1;
      // Phases added after the same phase keep the order they were proposed in.
      while (at < draft.phases.length && !phasesAsProposed.some((phase) => phase === draft.phases[at])) at += 1;
      const startCycle = draft.phases.slice(0, at).reduce((total, phase) => total + phase.cycles, 0) + 1;
      const phase = phaseFromSaveShape(change.phase, picks, startCycle);
      return phase !== null && insertPhaseDraft(draft, at, phase);
    }
    case 'phaseRemove': {
      const index = phaseIndexAt(change.phasePosition);
      removePhase(draft, index);
      return index !== -1 && phaseIndexAt(change.phasePosition) === -1;
    }
  }
}

/**
 * Apply the changes the trainer accepted, with everything they depend on, as one edit of the tree —
 * the store runs it in one producer, so one undo takes the whole proposal back (R19).
 *
 * Left out, whatever was accepted: an outdated change (R18), a change bringing in an exercise whose
 * library row the proposal did not carry (`picks`), and every change depending on either. Skipped, but
 * not taking its dependents with it: a change whose result already stands (applied before; again it
 * would duplicate its slot tokens). Each change goes through the
 * builder's own operations, so a change the builder refuses (a full day, a phase the client has
 * begun) is refused here too, and is not in the answer.
 *
 * Answers the ids of the changes that were made.
 */
export function applyProposalChanges(
  draft: MutablePlan,
  proposal: AiProposal,
  accepted: Iterable<string>,
): ReadonlySet<string> {
  const { picks } = proposal;
  const unpicked = proposal.changes
    .filter((change) => arrivingExerciseIds(change).some((id) => !picks.has(id)))
    .map((change) => change.id);
  // An outdated change and one whose library row is missing are not made, nor anything needing them.
  const blocked = withDependents(proposal.changes, [...outdatedChanges(proposal, draft), ...unpicked]);
  // One already in the tree is not made again; what depends on it finds it there.
  const present = presentChanges(proposal, draft);
  const chosen = withDependencies(proposal.changes, accepted);
  const toApply = proposal.changes
    .filter((change) => chosen.has(change.id) && !blocked.has(change.id) && !present.has(change.id))
    .sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);

  // The phases as the proposal saw them, by position. Captured before anything moves them, because
  // every position a change names is a position in that draft.
  const phasesAsProposed = [...draft.phases];
  const applied = new Set<string>();
  for (const change of toApply) {
    if (applyOne(draft, change, phasesAsProposed, picks)) applied.add(change.id);
  }
  return applied;
}

// ── Values the AI wrote (R15) ────────────────────────────────────────────────────────────────

/** The set values a mark names: every value of an exercise Buddy brought in, or the cells it changed. */
type WrittenCells = 'all' | ReadonlySet<string>;

/** One exercise's mark: the fingerprint it had when Buddy wrote it, and which of its values Buddy wrote. */
interface ExerciseMark {
  readonly stamp: string;
  readonly cells: WrittenCells;
}

/** Exercise slot token → its mark. */
export type AiWrittenMarks = ReadonlyMap<string, ExerciseMark>;

export const NO_AI_WRITTEN_MARKS: AiWrittenMarks = new Map();

/** The set fields a mark can name: every prescribed value a set table draws in a cell. */
export type AiWrittenValueKey = Exclude<keyof SetDraft, 'localId' | 'setIndex' | 'setType' | 'prescribedTempo'>;

const VALUE_KEYS: readonly AiWrittenValueKey[] = [
  'prescribedWeight',
  'prescribedReps',
  'prescribedRpe',
  'prescribedRir',
  'prescribedRestTime',
  'prescribedDurationSeconds',
  'prescribedDistanceMeters',
  'prescribedRounds',
  'prescribedPercent',
  'prescribedSpeed',
];

function cellKey(setIndex: number, valueKey: string): string {
  return `${String(setIndex)}:${valueKey}`;
}

/** The cells whose value differs between two prescriptions of one exercise, set by set. */
function changedCells(before: ExerciseDraft, after: ExerciseDraft): ReadonlySet<string> {
  const cells = new Set<string>();
  for (const set of after.sets) {
    const was = before.sets.find((candidate) => candidate.setIndex === set.setIndex);
    for (const key of VALUE_KEYS) {
      if (set[key] !== null && set[key] !== was?.[key]) cells.add(cellKey(set.setIndex, key));
    }
  }
  return cells;
}

/** The exercises the applied changes wrote, each with the values Buddy wrote in it. */
function writtenExercises(
  proposal: AiProposal,
  applied: ReadonlySet<string>,
  before: PlanDraft,
  after: PlanDraft,
): { readonly slotToken: string; readonly cells: WrittenCells }[] {
  return proposal.changes
    .filter((change) => applied.has(change.id))
    .flatMap((change): { readonly slotToken: string; readonly cells: WrittenCells }[] => {
      switch (change.kind) {
        case 'exerciseAlter': {
          const was = findExerciseSite(before, change.exerciseSlotToken)?.exercise;
          const now = findExerciseSite(after, change.exercise.slotToken)?.exercise;
          return was === undefined || now === undefined ? [] : [{ slotToken: now.slotToken, cells: changedCells(was, now) }];
        }
        case 'exerciseReplace':
        case 'exerciseAdd':
          return [{ slotToken: change.exercise.slotToken, cells: 'all' as const }];
        case 'dayAdd':
          return (change.day.sessions ?? []).flatMap((session) =>
            (session.exercises ?? []).map((exercise) => ({ slotToken: exercise.slotToken, cells: 'all' as const })),
          );
        case 'phaseAdd':
          return (change.phase.days ?? []).flatMap((day) =>
            (day.sessions ?? []).flatMap((session) =>
              (session.exercises ?? []).map((exercise) => ({ slotToken: exercise.slotToken, cells: 'all' as const })),
            ),
          );
        default:
          return [];
      }
    });
}

/**
 * The marks after a proposal went from `before` into `after`: each exercise it wrote, stamped as it
 * now stands, with the values Buddy wrote in it (every value of one it brought in, the changed cells
 * of one it altered).
 */
export function markAiWritten(
  marks: AiWrittenMarks,
  before: PlanDraft,
  after: PlanDraft,
  proposal: AiProposal,
  applied: ReadonlySet<string>,
): AiWrittenMarks {
  const next = new Map(marks);
  for (const written of writtenExercises(proposal, applied, before, after)) {
    const site = findExerciseSite(after, written.slotToken);
    if (site !== null) next.set(written.slotToken, { stamp: exerciseFingerprint(site.exercise), cells: written.cells });
  }
  return next;
}

/**
 * Whether one set value still holds what Buddy wrote. The trainer's edit of the exercise changes its
 * fingerprint, and from then on its values are theirs; an undo that takes the exercise away takes the
 * mark with it, and a redo that brings it back brings the mark back.
 */
export function isAiWrittenValue(
  marks: AiWrittenMarks,
  draft: PlanDraft,
  exerciseSlotToken: string,
  setIndex: number,
  valueKey: AiWrittenValueKey,
): boolean {
  const mark = marks.get(exerciseSlotToken);
  if (mark === undefined) return false;
  if (mark.cells !== 'all' && !mark.cells.has(cellKey(setIndex, valueKey))) return false;
  const site = findExerciseSite(draft, exerciseSlotToken);
  return site !== null && site.exercise.sets.some((set) => set.setIndex === setIndex && set[valueKey] !== null) && mark.stamp === exerciseFingerprint(site.exercise);
}
